import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  CampaignInvite,
  FunnelStats,
  computeFunnel,
  getCampaignInvites,
} from '../../services/campaignInvite'
import campaignInviteStore from '../../../mobx/campaignInvite'

// Charge les invitations d'une marque et expose le funnel agrege + un index par
// createur (pour badge "Invite(e)" et carte contexte dans la conversation).
// Se rafraichit a chaque envoi/achat via campaignInviteStore.balanceVersion.
export interface UseCampaignInvites {
  invites: CampaignInvite[]
  loading: boolean
  byUser: Map<string, CampaignInvite[]>
  invitedUserIds: Set<string>
  stats: FunnelStats
  refresh: () => void
}

export const useCampaignInvites = (brandId?: string): UseCampaignInvites => {
  const [invites, setInvites] = useState<CampaignInvite[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const version = campaignInviteStore.balanceVersion

  const refresh = useCallback(() => {
    if (!brandId) return
    setLoading(true)
    getCampaignInvites(brandId)
      .then((res) => setInvites(res))
      .catch(() => setInvites([]))
      .finally(() => setLoading(false))
  }, [brandId])

  useEffect(() => {
    let alive = true
    if (!brandId) {
      setInvites([])
      return
    }
    setLoading(true)
    getCampaignInvites(brandId)
      .then((res) => {
        if (alive) setInvites(res)
      })
      .catch(() => {
        if (alive) setInvites([])
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [brandId, version])

  const byUser = useMemo(() => {
    const map = new Map<string, CampaignInvite[]>()
    invites.forEach((i) => {
      const list = map.get(i.user) ?? []
      list.push(i)
      map.set(i.user, list)
    })
    return map
  }, [invites])

  const invitedUserIds = useMemo(
    () => new Set(byUser.keys()),
    [byUser]
  )

  const stats = useMemo(() => computeFunnel(invites), [invites])

  return { invites, loading, byUser, invitedUserIds, stats, refresh }
}
